
import { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile } from '@/types';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  userProfile: UserProfile | null;
  signOut: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  isLoadingProfile: boolean;
  /** True once the initial getSession() has resolved. Use this to gate
   *  auth-protected redirects so we don't bounce users to /candidates
   *  during the brief window before the session hydrates. */
  isAuthReady: boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState<boolean>(false);
  const [isAuthReady, setIsAuthReady] = useState<boolean>(false);

  useEffect(() => {
    console.log('AuthProvider: Setting up auth state listener');
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log('Auth state change:', event, currentSession?.user?.id);
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          // Check if this is a recruiter user - if so, don't fetch job seeker profile
          const userType = currentSession.user.user_metadata?.user_type;
          if (userType === 'recruiter') {
            console.log('Recruiter user detected, skipping job seeker profile fetch');
            setUserProfile(null);
            setIsLoadingProfile(false);
            return;
          }
          
          // Use setTimeout to prevent potential deadlock
          setTimeout(() => {
            fetchUserProfile(currentSession.user.id);
          }, 0);

          // Link any orphaned job alerts to this user on sign in
          if (event === 'SIGNED_IN' && currentSession.user.email) {
            setTimeout(() => {
              console.log('Linking orphaned job alerts for:', currentSession.user.email);
              supabase.functions.invoke('link-user-alerts', {
                body: {
                  userId: currentSession.user.id,
                  email: currentSession.user.email,
                },
              }).then(({ data, error }) => {
                if (error) {
                  console.warn('Link user alerts error:', error);
                } else if (data?.linkedCount > 0) {
                  console.log(`Linked ${data.linkedCount} orphaned job alerts to user`);
                }
              }).catch(console.warn);
            }, 100);
          }
        } else {
          setUserProfile(null);
          setIsLoadingProfile(false);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(async ({ data: { session: currentSession } }) => {
      console.log('Initial session check:', currentSession?.user?.id);
      setSession(currentSession);
      setUser(currentSession?.user ?? null);

      if (currentSession?.user) {
        // Check if this is a recruiter user - if so, don't fetch job seeker profile
        const userType = currentSession.user.user_metadata?.user_type;
        if (userType === 'recruiter') {
          console.log('Recruiter user detected in initial session, skipping job seeker profile fetch');
          setUserProfile(null);
          setIsLoadingProfile(false);
          setIsAuthReady(true);
          return;
        }
        
        await fetchUserProfile(currentSession.user.id);
      } else {
        setIsLoadingProfile(false);
      }
      setIsAuthReady(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserProfile = async (userId: string) => {
    setIsLoadingProfile(true);
    try {
      console.log('Fetching user profile for:', userId);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "Row not found" error
        console.error("Error fetching user profile:", error);
        throw error;
      }

      if (data) {
        // Convert the database result to match our UserProfile type
        const profile: UserProfile = {
          id: data.id,
          email: user?.email || '', // Add the required email field
          name: data.name || data.full_name || '',
          full_name: data.full_name,
          avatar_url: data.avatar_url,
          username: data.username,
          currentEmployer: data.currentemployer || '',
          currentTitle: data.currenttitle || '',
          desiredTitle: data.desiredtitle || '',
          desiredCompensation: data.desiredcompensation || '',
          softwareExperience: data.softwareexperience || [],
          yearsExperience: data.yearsexperience || 0,
          isCPA: data.iscpa || false,
          resume: data.resume || undefined,
          location: data.location || '',
          desiredLocation: data.desiredlocation || '', // NEW: map to desiredLocation
          bio: data.bio || '',
          shareWithRecruiters: data.sharewithrecruiters || false,
          monthEndCloserAccess: data.monthendcloseraccess || false,
          openToRemote: data.opentoremote || false,
          created_at: data.created_at,
          updated_at: data.updated_at
        };
        
        console.log('User profile fetched successfully:', profile.name);
        setUserProfile(profile);
      } else {
        console.log('No profile found for user');
        // We still want to have email available for the form on first visit
        if (user?.email) {
          setUserProfile({
            id: userId,
            email: user.email,
            monthEndCloserAccess: true, // CHANGED: reflect backend default immediately
          });
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  useEffect(() => {
    if (!session?.user || !session.user.email) return;

    try {
      const userId = session.user.id;

      // Month-End Closers: explicit opt-in only (LEAD-05, 2026-10-20). This used to subscribe EVERY signed-in
      // user on first sign-in, magic-link sign-ins from the apply flow included, with nothing asked beyond the
      // Terms checkbox. Now only an account created with the unticked "Also send me Month-End Closers" box on
      // /sign-up (user_metadata.mec_opt_in, src/pages/auth/SignUp.tsx) is subscribed, once per browser. The
      // profile form's Month-End Closers toggle (useProfileSubmit) stays the other explicit route.
      const mecKey = `mec-subscribed:${userId}`;
      const mecOptIn = (session.user.user_metadata as Record<string, unknown> | undefined)?.mec_opt_in === true;
      if (mecOptIn && !localStorage.getItem(mecKey)) {
        console.log('Auto-subscribing Month-End Closers for:', session.user.email);
        supabase.functions.invoke('add-beehiiv-subscriber', {
          body: {
            email: session.user.email,
            name: userProfile?.name || userProfile?.full_name || session.user.email,
          },
        }).then(({ data, error }) => {
          if (error) {
            console.warn('Beehiiv MEC subscribe error:', error);
          } else {
            console.log('Beehiiv MEC subscribe result:', data);
            if (!data || data.success !== false) {
              localStorage.setItem(mecKey, '1');
            }
          }
        });
      }

      // Only continue with specific subscriptions if we have userProfile
      if (!userProfile) return;

      // Open to Remote auto-sync on first profile load
      const remoteKey = `remote-synced:${userId}`;
      if (userProfile.openToRemote && !localStorage.getItem(remoteKey)) {
        console.log('Auto-syncing remote preferences for:', session.user.email);
        supabase.functions.invoke('sync-remote-preferences', {
          body: {
            email: session.user.email,
            name: userProfile.name || userProfile.full_name,
            currentTitle: userProfile.currentTitle,   // FIXED: align param names
            desiredTitle: userProfile.desiredTitle,   // FIXED: align param names
            openToRemote: true,
          },
        }).then(({ data, error }) => {
          if (error) {
            console.warn('Beehiiv remote sync error:', error);
          } else {
            console.log('Beehiiv remote sync result:', data);
            if (!data || data.success !== false) {
              localStorage.setItem(remoteKey, '1');
            }
          }
        });
      }
    } catch (e) {
      console.warn('Auto-subscribe/sync failed:', e);
    }
  }, [session?.user?.id, session?.user?.email, userProfile?.openToRemote]);

  const refreshUserProfile = async () => {
    if (user?.id) {
      await fetchUserProfile(user.id);
    }
  };

  const signOut = async () => {
    console.log('Signing out user');
    await supabase.auth.signOut();
    setUserProfile(null);
  };

  return (
    <AuthContext.Provider value={{ 
      session, 
      user, 
      userProfile, 
      signOut, 
      refreshUserProfile,
      isLoadingProfile,
      isAuthReady,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
